import * as React from "react";
import styled from "styled-components";
import Layout from "../../components/Layout";

interface Level {
  1: "familiar";
  2: "proficient";
  3: "intermediate";
  4: "advanced";
  5: "native";
}

interface Skill {
  id: number;
  name: string;
  level: 1 | 2 | 3 | 4 | 5;
}

interface Job {
  order: number;
  title: string;
  company: string;
  tenure: string;
  bullets: string[];
}

const levels: Level = {
  1: "familiar",
  2: "proficient",
  3: "intermediate",
  4: "advanced",
  5: "native",
};

const skills: Skill[] = [
  { id: 0, name: "English", level: 5 },
  { id: 1, name: "Portuguese", level: 5 },
  { id: 2, name: "SQL", level: 4 },
  { id: 3, name: "Python", level: 4 },
  { id: 4, name: "JavaScript/TypeScript", level: 4 },
  { id: 5, name: "React", level: 3 },
  { id: 6, name: "Go", level: 3 },
  { id: 10, name: "DBT || SQLMesh", level: 4 },
  { id: 11, name: "DuckDB", level: 3 },
  { id: 12, name: "Elixir", level: 2 },
];

const jobs: Job[] = [
  {
    order: 0,
    title: "Full Stack Engineer",
    company: "Baton",
    tenure: "April 2024 – Present",
    bullets: [
      "AI: Automate processes, build semantic search and other user-facing features, and accelerate software development.",
      "Data Platform: Preparing and providing the data needed for successful AI feautres. Building pipelines, designing models, and guiding analytics.",
      "Front-end: Deliver front-end features, contributed to migration from Next.js to Vite + Tanstack.",
      "Back-end: Deliver back-end features, with a focus on the events system that powers integrations.",
    ],
  },
  {
    order: 1,
    title: "Senior Data Engineer",
    company: "Remote",
    tenure: "Nov 2020 – April 2024",
    bullets: [
      "1st data hire: internal tools, automations, pipelines, reporting",
      "Designed, built, and implemented auditing tools and automations to ensure data quality in critical manual processes.",
      "Turned an ad hoc audit log into reliable structured data for analysts to more easily report on changes to records.",
      "Scaled data infrastructure to meet the needs of the company as it grew from 50 employees to > 1,000",
    ],
  },
  {
    order: 2,
    title: "Engineering Manager",
    company: "Embarc Collective",
    tenure: "Aug 2018 – Nov 2020",
    bullets: [
      "Responsible for all internal tools and reporting.",
      "Resource for over 50 early-stage startups, helping them work through problems from process automation to product & engineering strategy.",
      "Built several public-facing projects, including tampabaystartupjobs.com, tampabayeats.org, and reports.embarccollective.com. Also wrote the reports.",
    ],
  },
  {
    order: 3,
    title: "Software Engineer",
    company: "Freelancer",
    tenure: "Jun 2018 – Aug 2018",
    bullets: [
      "Focused on clients that needed to extend or alter the functionality of their off-the-shelf CRMs.",
      "Notable project: designed & built a chrome extension for a research team at UC Berkeley that needed to hide or modify content on a customer support interface.",
      "Consistently hit milestones ahead of time and under budget.",
    ],
  },
  {
    order: 4,
    title: "Engineering Manager",
    company: "Pack Health",
    tenure: "Aug 2016 – Jun 2018",
    bullets: [
      "Joined as a 2016 Venture for America Fellow.",
      "Started as a product associate, pivoted to engineering and led a shift to building software internally.",
      "Designed and built a patient-facing education and survey platform, leading to higher patient engagement and course completion (also did voiceovers for the videos).",
      "Built automated tools to help health coaches more efficiently engage with their patients.",
      "Helped hire a team and hand off my responsibilities as my 2-year fellowship came to a close.",
    ],
  },
];

const Skill = styled.li`
  text-decoration: none;
  margin: 10px 10px 0 0;
  padding: 4px 10px;
  color: var(--color-background);
  background-color: var(--color-secondary);
  border-radius: 4px;
  font-size: 0.9rem;
  line-height: 1.4;
  white-space: nowrap;
  
  @media print {
    margin: 0.1em 0.2em 0.1em 0;
    padding: 0.1em 0.3em;
    font-size: 0.7em;
    background-color: #f0f0f0 !important;
    color: black !important;
    border: 1px solid #ccc;
  }
`;

const SkillsHeader = styled.div`
  h2 {
    margin: 0 0 0.5rem 0;
  }
`;

const SeeMoreButton = styled.button`
  background: none;
  border: none;
  color: var(--color-secondary);
  cursor: pointer;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  padding: 0;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }
`;

const SkillsContainer = styled.div`
  position: relative;
`;

const Skills = styled.ul`
  min-width: 250px;
  flex-wrap: wrap;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: row;
  list-style-type: none;
  
  @media print {
    margin: 0.1em 0;
  }
`;

const Resume = styled.div`
  margin: 1rem;
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  
  @media print {
    margin: 0;
    padding: 0;
    max-width: none;
    width: 100%;
    height: auto;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  flex-wrap: wrap;
  
  @media print {
    display: flex;
    margin: 0.1em 0;
    width: 100%;
    
    &.skills-education-row {
      flex-direction: row;
      justify-content: space-between;
    }
    
    &:not(.skills-education-row) {
      flex-direction: column;
    }
  }
`;

const RowItem = styled.div`
  margin: 1rem;
  
  @media print {
    margin: 0.2em 0.5em;
  }
`;

const Education = styled(RowItem)`
  min-width: 250px;
  h1,
  h2,
  h3,
  h4 {
    margin-top: 0;
    margin-bottom: 10px;
  }
`;

const Title = styled(RowItem)`
  h1 {
    margin-bottom: 5px;
  }
  h3 {
    margin-top: 5px;
    margin-bottom: 5px;
  }
  p {
    margin-top: 7px;
    margin-bottom: 0;
  }
`;

const Job = styled(Row)`
  align-items: flex-start;
  margin: 1rem 1rem 1rem 0;
  
  @media print {
    page-break-inside: avoid;
    break-inside: avoid;
    margin: 0.15em 0;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const JobTitle = styled(RowItem)`
  margin-left: 0;
  h2,
  h4,
  p {
    margin: 10px 0;
  }
  
  .job-header {
    display: block;
  }
  
  .print-date {
    display: none;
  }
  
  .print-title-company {
    display: none;
  }
  
  .non-print-title {
    display: inline;
  }
  
  .company-name {
    display: block;
  }
  
  .non-print-date {
    display: block;
  }
  
  @media print {
    margin: 0;
    width: 100%;
    
    .job-header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      margin-bottom: 0.1em;
    }
    
    .print-date {
      display: block;
      margin: 0;
    }
    
    .print-title-company {
      display: inline;
    }
    
    .non-print-title {
      display: none;
    }
    
    .company-name {
      display: none;
    }
    
    .non-print-date {
      display: none;
    }
    
    h2 {
      margin: 0;
    }
    
    p {
      margin: 0;
    }
  }
`;

const JobDetails = styled(RowItem)`
  width: 60%;
  min-width: 300px;
  ul {
    padding: 0 1rem;
  }
  
  li {
    line-height: 1.4;
    margin-bottom: 0.5em;
  }
  
  @media print {
    width: 100%;
    min-width: auto;
    margin: 0;
    margin-top: 0.1em;
    
    ul {
      margin-top: 0.1em;
      padding: 0 1rem;
    }
    
    li {
      line-height: 1.2;
      margin-bottom: 0.1em;
    }
  }
`;

const PrintButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: var(--color-secondary);
  color: var(--color-background);
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  @media print {
    display: none;
  }
`;

const PrintableResume = styled.div`
  @media print {
    margin: 0;
    padding: 0;
    width: 100% !important;
    max-width: none !important;
    color: black !important;
    
    h1 {
      font-size: 16px !important;
      color: black !important;
    }
    
    h2, h3, h4 {
      font-size: 14px !important;
      color: black !important;
    }
    
    p, li, span {
      font-size: 12px !important;
      color: black !important;
    }
    
    /* Better page break control */
    .job-entry {
      page-break-inside: avoid;
      break-inside: avoid;
      margin-bottom: 0.3em;
    }
    
    /* Prevent orphaned headers */
    h2 {
      page-break-after: avoid;
    }
    
    ${SeeMoreButton} {
      display: none;
    }
  }
`;

const ResumePage: React.FC = () => {
  const [skillsExpanded, setSkillsExpanded] = React.useState(false);
  const [isPrinting, setIsPrinting] = React.useState(false);

  // Show all skills when printing
  const sortedSkills = skills.sort((a, b) => b.level - a.level);
  const visibleSkills = skillsExpanded
    ? sortedSkills
    : sortedSkills.slice(0, 11); // Show 11 skills initially
  const hasMoreSkills = sortedSkills.length > 11;

  // Show only first 5 jobs when printing
  const sortedJobs = jobs.sort((a, b) => a.order - b.order);
  const visibleJobs = isPrinting ? sortedJobs.slice(0, 5) : sortedJobs;

  const onPrintClick = () => {
    setSkillsExpanded(true);
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        setSkillsExpanded(false);
        setIsPrinting(false);
      }, 500);
    }, 100);
  };

  return (
    <Layout pageTitle="Fabio de Sousa's Résumé">
      <PrintableResume>
        <Resume>
        <Row>
          <Title>
            <h1>Fabio de Sousa - Software Engineer</h1>
            <p>Open to remote work.</p>
          </Title>
        </Row>
        <Row className="skills-education-row">
          <RowItem style={{ flex: "1 1 0" }} className="skill-section">
            <SkillsHeader>
              <h2>Skills</h2>
            </SkillsHeader>
            <SkillsContainer>
              <Skills>
                {visibleSkills.map((skill) => (
                  <Skill key={skill.id} title={levels[skill.level]}>
                    {skill.name}
                  </Skill>
                ))}
              </Skills>
              {hasMoreSkills && (
                <SeeMoreButton
                  onClick={() => setSkillsExpanded(!skillsExpanded)}
                >
                  {skillsExpanded ? "see less" : "see more"}
                </SeeMoreButton>
              )}
            </SkillsContainer>
          </RowItem>
          <Education style={{ flex: "1 1 0" }} className="education-section">
            <h2>Education</h2>
            <h4>Columbia University, May 2016</h4>
            <p>BA in Sociology, Magna cum laude</p>
          </Education>
        </Row>
        <Row>
          <RowItem>
            {visibleJobs.map((job) => (
                <Job key={job.order} className="job-entry">
                  <JobTitle>
                    <div className="job-header">
                      <h2><span className="print-title-company">{job.title} - {job.company}</span><span className="non-print-title">{job.title}</span></h2>
                      <p className="print-date">{job.tenure}</p>
                    </div>
                    <h4 className="company-name">{job.company}</h4>
                    <p className="non-print-date">{job.tenure}</p>
                  </JobTitle>
                  <JobDetails>
                    <ul>
                      {job.bullets.map((bullet, index) => (
                        <li key={index}>{bullet}</li>
                      ))}
                    </ul>
                  </JobDetails>
                </Job>
              ))}
            {isPrinting && (
              <p style={{ marginTop: '1rem', fontSize: '12px', textAlign: 'center' }}>
                {visibleJobs.length === jobs.length 
                  ? 'Printed from https://fabio.desousa.studio/work/resume'
                  : 'For full work experience, see https://fabio.desousa.studio/work/resume'
                }
              </p>
            )}
          </RowItem>
        </Row>
        </Resume>
      </PrintableResume>
      <PrintButton onClick={onPrintClick}>
        🖨️ Print Resume
      </PrintButton>
    </Layout>
  );
};

export default ResumePage;
